/// useUserTasks: React hook that acts as a JobWorker for user-task type jobs.
/// Polls the BPMN engine for user-task jobs, adds them to engineStore as
/// UserTask entries, and provides completeTask/failTask actions.

import { useEffect, useRef, useCallback } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import { useEngineStore, type UserTask } from "@/stores/engineStore";
import type { ActivateJobsResponse, CompleteJobResponse, FailJobResponse } from "@/protocol";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for managing user task lifecycle.
 * Polls the BPMN engine worker for "user-task" type jobs, adds activated jobs
 * to the engineStore as UserTask entries, and provides complete/fail actions
 * that send commands to the engine and remove tasks from the store.
 */
export function useUserTasks() {
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  // Start polling for user-task jobs
  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    const poll = async () => {
      if (!isPollingRef.current) return;

      try {
        const manager = getWorkerManager();
        const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
        if (!engineWorkerId) {
          // Engine not yet registered, retry later
          pollingRef.current = setTimeout(poll, 2000);
          return;
        }

        const result = await manager.sendCommand(
          engineWorkerId,
          {
            type: "activateJobs",
            direction: "cmd" as const,
            payload: {
              job_type: "user-task",
              worker_id: "react-ui-task-panel",
              max_jobs: "10",
            },
          } as never,
          10000,
        );

        const respPayload = result.payload as ActivateJobsResponse["payload"];
        if (!respPayload.error && respPayload.jobs) {
          try {
            const jobs = JSON.parse(respPayload.jobs) as {
              job_key: string;
              job_type: string;
              variables: Record<string, unknown>;
              custom_headers: Record<string, unknown>;
            }[];

            for (const job of jobs) {
              const task: UserTask = {
                jobKey: String(job.job_key),
                formKey: (job.custom_headers?.formKey as string) ?? "default",
                taskName: (job.custom_headers?.taskName as string) ?? "User Task",
                variables: job.variables ?? {},
                customHeaders: job.custom_headers ?? {},
                assignee: job.custom_headers?.assignee as string | undefined,
                candidateGroups: job.custom_headers?.candidateGroups as string[] | undefined,
                activatedAt: Date.now(),
              };
              useEngineStore.getState().addUserTask(task);
            }
          } catch {
            // JSON parse failure, ignore
          }
        }
      } catch {
        // Silently retry on next poll
      }

      if (isPollingRef.current) {
        pollingRef.current = setTimeout(poll, 2000);
      }
    };

    poll();
  }, []);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Complete a user task: sends completeJob to engine and removes from store
  const completeTask = useCallback(async (jobKey: string, variables: Record<string, unknown>) => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) {
      throw new Error("No BPMN engine worker is running");
    }

    const result = await manager.sendCommand(
      engineWorkerId,
      {
        type: "completeJob",
        direction: "cmd" as const,
        payload: {
          job_key: String(jobKey),
          variables: JSON.stringify(variables),
        },
      } as never,
      10000,
    );

    const respPayload = result.payload as CompleteJobResponse["payload"];
    if (respPayload.error) {
      throw new Error(respPayload.error);
    }

    useEngineStore.getState().removeUserTask(jobKey);
  }, []);

  // Fail a user task: sends failJob to engine and removes from store
  const failTask = useCallback(async (jobKey: string, reason: string) => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) {
      throw new Error("No BPMN engine worker is running");
    }

    const result = await manager.sendCommand(
      engineWorkerId,
      {
        type: "failJob",
        direction: "cmd" as const,
        payload: {
          job_key: String(jobKey),
          error_message: reason,
        },
      } as never,
      10000,
    );

    const respPayload = result.payload as FailJobResponse["payload"];
    if (respPayload.error) {
      throw new Error(respPayload.error);
    }

    useEngineStore.getState().removeUserTask(jobKey);
  }, []);

  // Start polling when mounted, stop on unmount
  useEffect(() => {
    startPolling();
    return stopPolling;
  }, [startPolling, stopPolling]);

  return { completeTask, failTask, startPolling, stopPolling };
}
